import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import PosItemSkeleton from "./PosItemSkeleton";
import AddItem from "../components/Items/addItem";
import { CART_DRAWER_WIDTH } from "./cartLayout";

import { StaffCommissionDetailContext } from "../Context/StaffCommissionDetailsContext";
import {
  AppBar,
  Toolbar,
  Typography,
  Card,
  CardContent,
  IconButton,
  Grid,
  CardMedia,
  InputBase,
  Badge,
  ButtonBase,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableHeader,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import { getAllStaff, getStaffByUser } from "../actions/staffActions";
import { Autocomplete } from "@material-ui/lab";
import { ItemContext } from "../Context/ItemContext";
import NoData from "../components/svgIcons/NoData";
import { useSnackbar } from "../utils/useSnackbar";
import { rankItems } from "../utils/itemRank";
import { useDispatch, useSelector } from "react-redux";
import { useCart } from "../Context/CartContext";
import { StockContext } from "../Context/StockContext";
import { useOrderSession } from "../Context/OrderSessionContext";
import useExpiryHints from "./useExpiryHints";
import BatchPickerDialog from "../components/common/BatchPickerDialog";
import Tip from "../components/common/Tip";
import { formatExpiry } from "../components/common/ExpiryChip";
import { neededInStockUnit, validPicks } from "../utils/batchPicks";
import LayersOutlinedIcon from "@material-ui/icons/LayersOutlined";
import EventBusyOutlinedIcon from "@material-ui/icons/EventBusyOutlined";
import ScheduleIcon from "@material-ui/icons/Schedule";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import RemoveShoppingCartOutlinedIcon from "@material-ui/icons/RemoveShoppingCartOutlined";
import { useItemSearch } from "../Context/ItemSearchContext";
import { useShift } from "../Context/ShiftContext";
import { useDiscount } from "../Context/DiscountContext";
import { useCurrency } from "../Context/CurrencyContext";
import { getAllItems } from "../actions/itemActions";
import AddIcon from "@material-ui/icons/Add";
import LocalMallOutlinedIcon from "@material-ui/icons/LocalMallOutlined";
import CommissionDialog from "./CommissionDialog";

// Layout shared by the frosted and solid variants of the card's bottom bar,
// so the two can never drift apart in size, radius or padding.
const cardContentBase = (theme) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-end",
  minHeight: "44%",
  padding: theme.spacing(0.5, 0.75, 0.75),
  pointerEvents: "none",
  boxSizing: "border-box",
  [theme.breakpoints.down("xs")]: {
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    padding: theme.spacing(0.25, 0.5, 0.5),
    minHeight: "46%",
  },
});

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginRight: -CART_DRAWER_WIDTH,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0, // When cart is open, no margin
  },

  title: {
    flexGrow: 1,
  },
  hide: {
    display: "none",
  },

  itemCard: {
    position: "relative",
    width: "100%",
    height: "auto",
    /* Compact without making the product grid feel crowded. */
    aspectRatio: "4 / 3",
    marginBottom: 0,
    backgroundColor: "#ffffff",
    cursor: "pointer",
    borderRadius: 12,
    border: "1px solid rgba(226, 232, 240, 0.85)",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
    transition: "transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      borderColor: "#fdba74",
      boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
    },
    [theme.breakpoints.down("xs")]: {
      borderRadius: 10,
    },
  },
  // The backdrop-filter glass on cardContent doesn't reliably respect
  // border-radius clipping when it shares an element with a hover
  // transform (a known Chrome/Safari quirk). Keeping the transform on
  // itemCard above but the overflow/radius clipping on this separate,
  // never-transformed wrapper avoids the glass bleeding past the corners.
  cardInner: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    borderRadius: 12,
    isolation: "isolate",
    [theme.breakpoints.down("xs")]: {
      borderRadius: 10,
    },
  },

  itemGrid: {
    display: "flex",
    flexBasis: "50%",
    maxWidth: "50%",
    // Lets the browser skip layout/paint for cards scrolled out of view
    // (ignored by browsers that don't support it). The intrinsic size is
    // only a placeholder height until a card has rendered once.
    contentVisibility: "auto",
    containIntrinsicSize: "auto 160px",
  },
  /* Desktop grid layout classes (auto-adjusted per user selection, default 6x5) */
  gridCols6: {
    [theme.breakpoints.up("md")]: {
      flexBasis: "16.666667%",
      maxWidth: "16.666667%",
    },
  },
  gridCols5: {
    [theme.breakpoints.up("md")]: {
      flexBasis: "20%",
      maxWidth: "20%",
    },
  },
  gridCols4: {
    [theme.breakpoints.up("md")]: {
      flexBasis: "25%",
      maxWidth: "25%",
    },
  },
  gridCols3: {
    [theme.breakpoints.up("md")]: {
      flexBasis: "33.333333%",
      maxWidth: "33.333333%",
    },
  },

  itemButton: {
    width: "100%",
    display: "block",
    borderRadius: 12,
    overflow: "hidden",
    textAlign: "left",
    [theme.breakpoints.down("xs")]: {
      borderRadius: 10,
    },
  },

  cardMedia: {
    position: "absolute",
    inset: 0,
    height: "100%",
    width: "100%",
    background: "linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%)",
  },

  // Sits behind the photo so it also covers a broken image URL, not just an
  // item that never had one.
  cardPlaceholder: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(145deg, #f8fafc 0%, #edf2f7 100%)",
    paddingBottom: "32%",
  },
  cardPlaceholderIconCircle: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#94a3b8",
    [theme.breakpoints.down("xs")]: {
      width: 36,
      height: 36,
    },
  },
  cardPlaceholderIcon: {
    fontSize: 24,
    [theme.breakpoints.down("xs")]: {
      fontSize: 20,
    },
  },

  // Bar used when a photo is behind it. Was also backdropFilter: blur(4px) -
  // a live blur on every photo card is one of the most expensive things a
  // browser can paint and made scrolling a big catalogue stutter on POS
  // tablets; under a gradient this dark it was barely visible anyway.
  cardContent: {
    ...cardContentBase(theme),
    background:
      "linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.6) 35%, rgba(15, 23, 42, 0.92) 100%)",
  },

  // No photo means there is no backdrop worth frosting.
  cardContentSolid: {
    ...cardContentBase(theme),
    background:
      "linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.65) 30%, rgba(15, 23, 42, 0.94) 100%)",
    borderTop: "none",
  },

  itemPriceBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(249, 115, 22, 0.96)",
    color: "#ffffff",
    fontSize: "0.78rem",
    fontWeight: 800,
    lineHeight: 1.15,
    padding: "2px 7px",
    borderRadius: 5,
    letterSpacing: "0.01em",
    fontVariantNumeric: "tabular-nums",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)",
    marginBottom: 3,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.7rem",
      padding: "1.5px 5px",
      marginBottom: 2,
    },
  },
  // Expiry hints along the top edge of a card: what is wrong (left) and how many batches (right).
  expiryTags: {
    position: "absolute",
    top: 6,
    left: 6,
    right: 6,
    zIndex: 2,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 4,
    pointerEvents: "none",
  },
  expiryPill: {
    pointerEvents: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    // Never a percentage of its own container (the container's width comes from the pill: circular, and
    // it shrank the pill to ~70% of its text). The container below carries the real limit.
    maxWidth: "100%",
    boxSizing: "border-box",
    flexShrink: 0,
    padding: "2px 8px 2px 5px",
    borderRadius: 9999,
    fontSize: "0.66rem",
    fontWeight: 800,
    lineHeight: 1.3,
    color: "#ffffff",
    whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
    "& svg": { fontSize: 13 },
  },
  // Room is left for the batch-count chip on the right; the pills inside size to their text.
  tagStack: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3, minWidth: 0, maxWidth: "calc(100% - 46px)" },
  pillText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  stockPillLow: { background: "rgba(37, 99, 235, 0.96)" },
  stockPillNone: { background: "rgba(71, 85, 105, 0.97)" },
  expiryPillExpired: { background: "rgba(220, 38, 38, 0.96)" },
  expiryPillNear: { background: "rgba(245, 158, 11, 0.97)", color: "#451a03" },
  expiryPillSoon: { background: "rgba(234, 88, 12, 0.97)" },
  batchPill: {
    pointerEvents: "auto",
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 7px 2px 5px",
    borderRadius: 9999,
    fontSize: "0.66rem",
    fontWeight: 800,
    lineHeight: 1.3,
    color: "#ffffff",
    background: "rgba(15, 23, 42, 0.78)",
    border: "1px solid rgba(255,255,255,0.35)",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background 0.15s ease, transform 0.15s ease",
    "& svg": { fontSize: 13 },
    "&:hover": { background: "#ea580c", transform: "scale(1.06)" },
    "&:focus-visible": { outline: "2px solid #fdba74" },
  },
  itemName: {
    color: "#ffffff",
    fontSize: "0.76rem",
    fontWeight: 600,
    marginBottom: 0,
    lineHeight: 1.2,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    maxWidth: "100%",
    textAlign: "center",
    textShadow: "0 1px 3px rgba(0, 0, 0, 0.7)",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.7rem",
      lineHeight: 1.15,
    },
  },

  // Dedicated Add Item card with modern dashed outline and tactile hover
  addCard: {
    position: "relative",
    width: "100%",
    aspectRatio: "4 / 3",
    borderRadius: 12,
    border: "2px dashed #cbd5e1",
    background: "linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      borderColor: "#f97316",
      background: "linear-gradient(145deg, #fff7ed 0%, #ffedd5 100%)",
      boxShadow: "0 8px 18px rgba(249, 115, 22, 0.15)",
      "& $addCardIconWrap": {
        backgroundColor: "#f97316",
        color: "#ffffff",
        transform: "scale(1.08)",
        boxShadow: "0 4px 12px rgba(249, 115, 22, 0.3)",
      },
      "& $addCardLabel": {
        color: "#ea580c",
      },
    },
    [theme.breakpoints.down("xs")]: {
      borderRadius: 10,
    },
  },
  addCardIconWrap: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#ffedd5",
    color: "#ea580c",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 6,
    transition: "all 0.18s ease",
    boxShadow: "0 2px 6px rgba(234, 88, 12, 0.12)",
    [theme.breakpoints.down("xs")]: {
      width: 34,
      height: 34,
      marginBottom: 4,
    },
  },
  addCardIcon: {
    fontSize: 22,
    [theme.breakpoints.down("xs")]: {
      fontSize: 18,
    },
  },
  addCardLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: "#475569",
    letterSpacing: "0.01em",
    transition: "color 0.18s ease",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.72rem",
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    minHeight: "60vh",
    padding: theme.spacing(4, 2),
  },
  emptyStateIconButton: {
    width: 96,
    height: 96,
    borderRadius: "50%",
    backgroundColor: "#fff3e6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
  },
  emptyStateIcon: {
    fontSize: 44,
    color: "#ef7f1a",
  },
  emptyStateTitle: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: "#1f2d3d",
    marginBottom: theme.spacing(0.75),
  },
  emptyStateSubtitle: {
    fontSize: "0.9rem",
    color: "#7f8c9a",
    maxWidth: 320,
    marginBottom: theme.spacing(3),
  },
  emptyStateButton: {
    backgroundColor: "#ef7f1a",
    color: "#fff",
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 10,
    padding: theme.spacing(1, 3),
    boxShadow: "0 8px 18px rgba(239, 127, 26, 0.28)",
    "&:hover": {
      backgroundColor: "#d96f17",
      boxShadow: "0 10px 22px rgba(239, 127, 26, 0.36)",
    },
  },
}));

// Extracted so React.memo can skip re-rendering (and re-diffing the
// CardMedia/backdrop-filter/gradient markup underneath) cards whose props
// haven't actually changed - the catalogue can run into the hundreds of
// items, and previously this whole markup was inline in ItemCards' render,
// so EVERY item re-rendered on every ItemCards render (adding something to
// the cart, a staff list refresh, ...) even though only the clicked item's
// own state ever changes. `item` stays referentially stable across those
// re-renders (it comes straight from the Redux items array via .filter(),
// which doesn't clone items), and `onSelect` is a stable ref-backed callback
// (see handleItemSelect below) - so this now actually bails out for the
// items that aren't affected.
const ItemCard = React.memo(function ItemCard({ item, currency, onSelect, gridCols = 6, hint, onPickBatch, canPick, showExpiry = true, showStock = true }) {
  const classes = useStyles();
  const colClass = classes[`gridCols${gridCols}`] || classes.gridCols6;
  // Expiry hint (only for expiry-tracked items that have expired / near-expiry stock, or 2+ batches).
  const unit = hint?.unit || "";
  const nearDays = hint?.near?.days;
  const pill =
    showExpiry && hint && hint.expired
      ? {
          cls: classes.expiryPillExpired,
          icon: <EventBusyOutlinedIcon />,
          text: "Expired stock",
          title: `${hint.expired.qty} ${unit} expired`,
          subtitle: `${hint.expired.batches} batch${hint.expired.batches === 1 ? "" : "es"} past the date${hint.near ? ` · ${hint.near.qty} ${unit} more expiring soon` : ""}`,
          tip: "Sell or write it off first",
        }
      : showExpiry && hint && hint.near
      ? {
          cls: nearDays <= 7 ? classes.expiryPillSoon : classes.expiryPillNear,
          icon: <ScheduleIcon />,
          text: nearDays <= 0 ? "Expires today" : `${nearDays}d left`,
          title: nearDays <= 0 ? "Expires today" : `Expires in ${nearDays} day${nearDays === 1 ? "" : "s"}`,
          subtitle: `Batch ${hint.near.batchNo} · ${formatExpiry(hint.near.expiryDate)} · ${hint.near.qty} ${unit}${hint.near.batches > 1 ? ` (+${hint.near.batches - 1} more batch${hint.near.batches === 2 ? "" : "es"})` : ""}`,
          tip: "It is sold first automatically",
        }
      : null;
  const showBatches = showExpiry && canPick && hint && hint.batchCount >= 2;
  // Stock tags are judged on SELLABLE stock (expired units are not counted).
  const stockPill =
    showStock && hint && hint.noStock
      ? {
          cls: classes.stockPillNone,
          icon: <RemoveShoppingCartOutlinedIcon />,
          text: "No stock",
          title: hint.expired ? "No sellable stock" : "Out of stock",
          subtitle: hint.expired ? `${hint.expired.qty} ${unit} left, all expired` : "Nothing in stock",
          tip: "Purchase or adjust stock to sell it",
        }
      : showStock && hint && hint.low
      ? {
          cls: classes.stockPillLow,
          icon: <TrendingDownIcon />,
          text: "Low stock",
          title: `Low stock: ${hint.sellableQty} ${unit} left`,
          subtitle: `Minimum is ${hint.minQty} ${unit}${hint.expired ? ` · ${hint.expired.qty} expired, not counted` : ""}`,
          tip: "Reorder soon",
        }
      : null;
  const pickBatch = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onPickBatch(item);
  };
  return (
    <Grid item xs={6} sm={6} className={`${classes.itemGrid} ${colClass}`}>
      <ButtonBase centerRipple className={classes.itemButton}>
        <Card className={classes.itemCard} onClick={() => onSelect(item)}>
          <div className={classes.cardInner}>
            {(pill || stockPill || showBatches) && (
              <div className={classes.expiryTags}>
                <div className={classes.tagStack}>
                  {pill && (
                    <Tip title={pill.title} subtitle={pill.subtitle} hint={pill.tip} placement="bottom">
                      <span className={`${classes.expiryPill} ${pill.cls}`}>
                        {pill.icon}
                        <span className={classes.pillText}>{pill.text}</span>
                      </span>
                    </Tip>
                  )}
                  {stockPill && (
                    <Tip title={stockPill.title} subtitle={stockPill.subtitle} hint={stockPill.tip} placement="bottom">
                      <span className={`${classes.expiryPill} ${stockPill.cls}`}>
                        {stockPill.icon}
                        <span className={classes.pillText}>{stockPill.text}</span>
                      </span>
                    </Tip>
                  )}
                </div>
                {showBatches && (
                  <Tip
                    title={`${hint.batchCount} batches in stock`}
                    subtitle={`${hint.totalQty} ${unit} in total`}
                    hint="Click to choose which batch to sell"
                    placement="bottom"
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      className={classes.batchPill}
                      onClick={pickBatch}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") pickBatch(e);
                      }}
                      aria-label={`Choose a batch of ${item.itemName}`}
                    >
                      <LayersOutlinedIcon />
                      {hint.batchCount}
                    </span>
                  </Tip>
                )}
              </div>
            )}
            <div className={classes.cardPlaceholder} aria-hidden="true">
              <div className={classes.cardPlaceholderIconCircle}>
                <LocalMallOutlinedIcon className={classes.cardPlaceholderIcon} />
              </div>
            </div>

            {item.img && (
              <CardMedia
                component="img"
                alt={item.itemName}
                className={classes.cardMedia}
                image={item.img}
                // Only download photos for cards near the screen, and decode
                // them off the main thread so taps don't wait on images.
                loading="lazy"
                decoding="async"
                style={{ objectFit: "cover", backgroundColor: "#fff" }}
                onError={(event) => {
                  // Reveal the placeholder underneath rather than
                  // swapping in a letterboxed stand-in image.
                  event.currentTarget.onerror = null;
                  event.currentTarget.style.display = "none";
                }}
              />
            )}

            <CardContent
              className={item.img ? classes.cardContent : classes.cardContentSolid}
            >
              <div className={classes.itemPriceBadge}>
                {currency}
                {/* toLocaleString rather than toCommas/toFixed(2) - those
                    force 2 decimals onto every price, but these cards have
                    always shown clean whole prices (฿10, not ฿10.00); this
                    only adds comma grouping, still showing real cents when
                    a price actually has them. */}
                {Number(item?.units?.sale?.price || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </div>
              <Typography variant="body2" className={classes.itemName}>
                {item.itemName}
              </Typography>
            </CardContent>
          </div>
        </Card>
      </ButtonBase>
    </Grid>
  );
});

// First screenful (the largest desktop layout shows ~30 cards) plus margin,
// then this many more per frame - see renderLimit in ItemCards.
const INITIAL_RENDER_COUNT = 48;
const RENDER_CHUNK_SIZE = 96;

const ItemCards = ({
  staffCommissionDetails,
  cartOpen = false,
  isCartPersistent = false,
  isActive = true,
  gridCols = 6,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    cartItems,
    handleAddToCart,
    setCartItems,
  } = useCart();
  const { searchQuery, selectedCategory, setSelectedCategory, clearSearch } =
    useItemSearch();
  const { isShiftBlocked, openShiftRequiredDialog } = useShift();
  const { getConvertedQuantity } = useContext(StockContext);
  const { activeSessionId } = useOrderSession();
  const { hints, showExpiry, showStock, pickMode } = useExpiryHints();
  const { staffCommissionDetailsArray, setStaffCommissionDetailsArray } =
    useContext(StaffCommissionDetailContext);
  const { itemId, setItemId } = useContext(ItemContext);
  const { clearDiscount } = useDiscount();
  const items = useSelector((state) => state.items.items);
  //console.log(items);
  const staff = useSelector((state) => state.staff.staff);
  const categories = useSelector((state) => state.categories.categories);

  // The new item is already in the store (ADD_NEW_ITEM), but the grid only
  // shows the active category tab + search - so creating an item in another
  // category (or with no category) while a tab/search was active made it
  // look like the item was never added. Switch to where it actually is.
  const handleItemCreated = useCallback(
    (createdItem) => {
      if (!createdItem?._id) return;
      const createdCategoryId = createdItem.category?.categoryId || null;
      if (selectedCategory && selectedCategory._id !== createdCategoryId) {
        setSelectedCategory(
          (createdCategoryId &&
            (categories || []).find((category) => category._id === createdCategoryId)) ||
            null
        );
      }
      clearSearch();
    },
    [selectedCategory, setSelectedCategory, categories, clearSearch]
  );
  // Was: initialized to `items.length === 0`, so a revisit with items
  // already cached from earlier in the session skipped the skeleton
  // entirely and committed the full (large) item grid synchronously on the
  // very first render - on a big catalogue that's exactly the render cost
  // that made navigating back to this screen feel like it hung for a beat.
  // Always starting with the skeleton (cheap to mount) and clearing it a
  // frame later - see the requestAnimationFrame effect below - guarantees
  // the browser paints something immediately, so navigation itself feels
  // instant even while the heavier grid is still being built underneath.
  const [loading, setLoading] = useState(true);
  // console.log(
  //   staff.filter((staff) =>
  //     staff.services.some((service) => service.id === "658ea97fcba8db44101c2ba6")
  //   )
  // );
  const { currency, updateCurrency } = useCurrency();
  const [imageSources, setImageSources] = useState({});
  const [openItem, setOpenItem] = useState(false);
  // No custom options here - the shared useSnackbar default already gives a
  // working close (X) button at bottom-right. The old `options` object this
  // used to pass was a leftover from the pre-react-toastify snackbar library
  // (position: "bottom" isn't a valid react-toastify position, and its
  // style/closeStyle fields were silently ignored) - that's what broke this
  // toast's positioning and close button.
  const [openSnackbar, closeSnackbar] = useSnackbar();
  // Was: an object keyed by every item's _id, with one full <CommissionDialog>
  // instance mounted per item regardless of whether it was open (see below) -
  // only one item's dialog is ever meaningfully open at a time anyway.
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState({});

  const [quantity, setQuantity] = useState(1);

  // const filteredItems = items.filter((item) =>
  //   item.itemName.toLowerCase().includes(searchQuery.toLowerCase())
  // );
  // const filteredItemsByCategory = selectedCategory
  //   ? filteredItems.filter(
  //       (item) => item.category.categoryId === selectedCategory._id
  //     )
  //   : filteredItems;
  // useEffect(() => {
  //   dispatch(getAllItems());
  // }, [dispatch]);


  // Memoized so re-renders that don't change items / search / category (cart
  // updates, dialogs, ...) don't re-run the fuzzy match over the whole
  // catalogue, and so the grid keeps the same array (and item refs) between them.
  const filteredItemsByCategory = useMemo(() => {
    const normalizedSearchQuery = String(searchQuery || "").trim().toLowerCase();
    const categoryId = selectedCategory?._id;
    const sellable = items.filter((item) => {
      // getItems returns soft-deleted items too (the Items page needs them
      // filtered, not missing) - they must never be sellable on the POS.
      if (!item.isActive || item.isDeleted) return false;
      if (selectedCategory && item.category?.categoryId !== categoryId) return false;
      return true;
    });
    if (!normalizedSearchQuery) return sellable;
    // Product name or the barcode on its label, best match first (exact barcode,
    // then name starts with, ... - see utils/itemRank.js).
    return rankItems(sellable, normalizedSearchQuery);
  }, [items, searchQuery, selectedCategory]);

  // Mounting hundreds of cards in one go (clearing a search, switching
  // category tab, first load) blocked the screen for a noticeable beat.
  // Render the first screenful straight away, then add the rest in chunks
  // on following frames so the grid is usable immediately. Only resets on
  // the user's own search/category changes - not when live updates or the
  // streamed catalogue change `items` - so already-shown cards never
  // unmount and remount.
  const [renderLimit, setRenderLimit] = useState(INITIAL_RENDER_COUNT);
  useEffect(() => {
    setRenderLimit(INITIAL_RENDER_COUNT);
  }, [searchQuery, selectedCategory]);
  useEffect(() => {
    if (renderLimit >= filteredItemsByCategory.length) return;
    const frame = requestAnimationFrame(() =>
      setRenderLimit((limit) => limit + RENDER_CHUNK_SIZE)
    );
    return () => cancelAnimationFrame(frame);
  }, [renderLimit, filteredItemsByCategory.length]);
  const visibleItems = useMemo(
    () =>
      renderLimit >= filteredItemsByCategory.length
        ? filteredItemsByCategory
        : filteredItemsByCategory.slice(0, renderLimit),
    [filteredItemsByCategory, renderLimit]
  );

  // getAllItems() now streams the catalog in progressively (first ~60 items,
  // then the rest in the background - see itemActions.js) instead of one
  // request for everything. Clearing the skeleton here as soon as the store
  // actually has items - rather than waiting on the dispatch promise, which
  // only resolves once every page has arrived - is what makes that streaming
  // actually show up sooner instead of still blocking on the full catalog.
  useEffect(() => {
    if (items.length === 0) return;
    // requestAnimationFrame (rather than clearing synchronously) gives the
    // browser a chance to actually paint the skeleton from the mount above
    // before the full grid replaces it, instead of both commits landing
    // together and the skeleton never becoming visible.
    const frame = requestAnimationFrame(() => setLoading(false));
    return () => cancelAnimationFrame(frame);
  }, [items]);

  useEffect(() => {
    if (items.length > 0) {
      // Already loaded from a previous visit - skip the refetch entirely.
      return;
    }
    // The effect above only clears the skeleton once items actually show up
    // in the store - which never happens for a genuinely empty catalog, so
    // without this the skeleton would spin forever instead of settling into
    // the real "no items" empty state.
    dispatch(getAllItems())
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    if (staff.length === 0) {
      dispatch(getAllStaff());
    }
    // dispatch(getStaffByUser({ search: user?.result?._id || user.result.googleId }));
    // eslint-disable-next-line
  }, [dispatch]);
  useEffect(() => {
    if (staffCommissionDetails) {
      setStaffCommissionDetailsArray(staffCommissionDetails);
    }
  }, [staffCommissionDetails]);
  // useEffect(() => {
  //   const getCurrentStockQty = (itemId) => async (dispatch) => {

  //     try {
  //       const { data } = await api.getCurrentStockQuantity(id);

  //     } catch (error) {
  //       console.log(error.response);
  //     }
  //   };
  //   getCurrentStockQty();

  // },[cartItems]);

  const handleCommissionDialogOpen = (service) => {
    setSelectedService(service);
    setQuantity(1);
    setCommissionDialogOpen(true);
  };
  const handleCommissionDialogClose = () => {
    setCommissionDialogOpen(false);
  };

  // Stable identity (useCallback + a ref for the actually-current values)
  // so passing this to the memoized ItemCard below doesn't itself defeat
  // the memoization - handleAddToCart/openShiftRequiredDialog are recreated
  // on every render (CartContext/ShiftContext aren't stabilized down to
  // that level), so without this every ItemCard would re-render whenever
  // either changed, e.g. every time an item is added to the cart.
  const itemSelectHandlersRef = useRef();
  itemSelectHandlersRef.current = {
    hints,
    pickMode,
    activeSessionId,
    cartItems,
    getConvertedQuantity,
    setCartItems,
    isShiftBlocked,
    openShiftRequiredDialog,
    handleAddToCart,
    quantity,
    handleCommissionDialogOpen,
  };
  // One physical tap can reach us twice (a bouncing mouse button, a touch screen's
  // tap + synthesized click, a scanner Enter on a focused card) and that quietly
  // put 2 in the cart. A person cannot tap the same card twice within ~150 ms, so
  // a repeat inside that window is dropped; normal fast tapping is unaffected.
  const lastSelectRef = useRef({ id: null, at: 0 });
  const pickBatchRef = useRef(null); // set below, once the chooser handler exists
  const handleItemSelect = useCallback((item) => {
    const now = Date.now();
    const last = lastSelectRef.current;
    if (item?._id && last.id === item._id && now - last.at < 150) {
      console.warn("Ignored a duplicate add of the same item within 150 ms:", item.itemName, now - last.at, "ms");
      return;
    }
    lastSelectRef.current = { id: item?._id, at: now };
    const {
      isShiftBlocked,
      openShiftRequiredDialog,
      handleAddToCart,
      quantity,
      handleCommissionDialogOpen,
    } = itemSelectHandlersRef.current;
    if (item.isStaffReq) {
      handleCommissionDialogOpen(item);
    } else if (isShiftBlocked) {
      openShiftRequiredDialog(item, quantity);
    } else {
      // Store setting "Which batch is sold at the till": Automatic adds straight away (nearest
      // expiry first); "Ask every time" / "Ask only when needed" open the batch chooser first when
      // the item has 2+ batches (when needed = it also has expired / near-expiry stock). Not on a
      // table / KOT bill, whose sales are automatic.
      const { hints: hintMap, pickMode: mode, activeSessionId: onTable } = itemSelectHandlersRef.current;
      const hint = hintMap && hintMap[item._id];
      const askFirst =
        !onTable &&
        item.trackExpiry === true &&
        hint &&
        hint.batchCount >= 2 &&
        (mode === "ask" || (mode === "askWhenNeeded" && (hint.expired || hint.near)));
      if (askFirst && pickBatchRef.current) {
        pickBatchRef.current(item);
        return;
      }
      // Search is deliberately kept after adding so the cashier can tap the
      // same (or another matching) card again without retyping.
      handleAddToCart(item, quantity);
    }
  }, []);

  // The "N batches" chip on a card: choose which batch(es) the sale takes from, THEN add. The
  // picker covers the whole cart line (what is already there + this add), and the choice is kept
  // on the line like one made from the cart. Not offered on a table / KOT bill (that sale is automatic).
  const [batchPick, setBatchPick] = useState(null);
  // The cashier decides the QUANTITY as well: any amount from any batches, several at once. The pop-up
  // starts from the cart line's current choice (or the automatic plan for what is there + this add)
  // and what is confirmed REPLACES the line's quantity and batches. Quantities in the pop-up are in
  // the item's stock unit; the cart line is in the sale unit (`unitStock` stock units = 1 sale unit).
  const confirmingRef = useRef(false);
  const handlePickBatch = useCallback(async (item) => {
    const h = itemSelectHandlersRef.current;
    if (h.isShiftBlocked) {
      h.openShiftRequiredDialog(item, h.quantity);
      return;
    }
    const line = h.cartItems.find((l) => String(l._id) === String(item._id));
    const base = Number(line?.quantity) || 0;
    const add = Number(h.quantity) || 1;
    const source = line || item;
    const unitStock = neededInStockUnit(h.getConvertedQuantity, { ...source, quantity: 1 }) || 1;
    // Nothing is pre-selected: the cashier decides. Only a choice already made on this cart line is kept.
    const initial = line ? validPicks(line, unitStock * base) : null;
    setBatchPick({ item: source, needed: unitStock * (base + add), initialPicks: initial || [], add, base, unitStock });
  }, []);
  pickBatchRef.current = handlePickBatch;
  const confirmBatchPick = async (picks) => {
    if (confirmingRef.current || !batchPick) return; // a double click must not add twice
    confirmingRef.current = true;
    const { item, add, base, unitStock } = batchPick;
    setBatchPick(null);
    const h = itemSelectHandlersRef.current;
    const sameLine = (line) => String(line._id) === String(item._id);
    try {
      if (!picks.length) {
        // "Use automatic": add what was asked for, nearest expiry first, with no fixed batches.
        await h.handleAddToCart(item, add);
        h.setCartItems((prev) => prev.map((line) => (sameLine(line) ? { ...line, batchPicks: undefined } : line)));
        return;
      }
      const selectedStock = picks.reduce((sum, p) => sum + (Number(p.qty) || 0), 0);
      const newTotal = Math.round((selectedStock / unitStock) * 1000) / 1000; // sale units
      const delta = newTotal - base;
      if (delta > 0.0005) await h.handleAddToCart(item, delta); // stock checks / table session as for any add
      h.setCartItems((prev) =>
        prev.map((line) => (sameLine(line) ? { ...line, ...(delta <= 0.0005 ? { quantity: newTotal } : {}), batchPicks: picks } : line))
      );
    } finally {
      confirmingRef.current = false;
    }
  };

  const handleBarcodeScan = (scannedValue, target) => {
    // Find the item with a matching barcode
    // Same sellable rule as the grid: a scan must not add an inactive or
    // deleted item that the grid itself hides.
    const code = String(scannedValue || "").trim();
    const sellable = items.filter((item) => item.isActive && !item.isDeleted);
    const lowerCode = code.toLowerCase();
    // Exact match first; case-insensitive fallback covers a scanner with
    // Caps Lock on or labels printed in the other case.
    const matchingItem =
      sellable.find((item) => String(item.barcode || "").trim() === code || item._id === code) ||
      sellable.find((item) => String(item.barcode || "").trim().toLowerCase() === lowerCode);
    if (matchingItem) {
      // Same path as tapping the card: staff-required items still get the
      // staff dialog, and the shift check still applies. Was a direct
      // handleAddToCart, which added staff-required services with no staff.
      handleItemSelect(matchingItem);
      // Only wipe the search when the scanner actually typed the barcode
      // into the search box - otherwise leave whatever the cashier searched.
      if (target && target.getAttribute && target.getAttribute("data-pos-search") === "true") {
        clearSearch();
      }
    } else {
      // 3000 = auto-close after 3 seconds specifically for this toast,
      // without changing the shared hook's default duration for every other
      // openSnackbar call in this file.
      openSnackbar("Item not found for the scanned barcode. Please check.", 3000);
    }
  };
  // Enter in the POS search box (typed by hand, not a fast scan): an exact
  // barcode, or the one and only match, is added just like tapping its card.
  const handleSearchEnter = () => {
    const query = String(searchQuery || "").trim().toLowerCase();
    if (!query || filteredItemsByCategory.length === 0) return;
    const exact = filteredItemsByCategory.find(
      (item) => String(item.barcode || "").trim().toLowerCase() === query
    );
    const pick = exact || (filteredItemsByCategory.length === 1 ? filteredItemsByCategory[0] : null);
    if (!pick) return;
    handleItemSelect(pick);
    clearSearch();
  };
  const handleSearchEnterRef = useRef(handleSearchEnter);
  handleSearchEnterRef.current = handleSearchEnter;

  // Kept up to date every render (cheap ref assignment, no effect) so the
  // listener below - registered once - always calls the latest handler
  // instead of a stale one from whenever the effect last ran.
  const handleBarcodeScanRef = useRef(handleBarcodeScan);
  handleBarcodeScanRef.current = handleBarcodeScan;

  // Global keydown listener for barcode-scanner input (fires characters
  // fast, then Enter). Was: no dependency array, so this tore down and
  // re-attached the document listener on every single render of a screen
  // that re-renders often - pure churn. A ref-based buffer lets this run
  // once (mount/unmount only) while still reacting to current cart/shift state.
  // Gated on isActive since PosMainScreen now stays mounted-but-hidden on
  // other routes (see PersistentPosMainScreen.js) - without this, barcode
  // scans would keep adding to the cart even while looking at another page.
  useEffect(() => {
    if (!isActive) return;
    let buffer = "";
    let resetTimer = null;
    function handleKeyDown(e) {
      // Keys typed inside a dialog belong to it - e.g. scanning a barcode
      // into Add Item's barcode field used to also run a POS scan here
      // ("Item not found" toast, or an unwanted add to the cart).
      if (e.target && e.target.closest && e.target.closest('[role="dialog"]')) {
        buffer = "";
        return;
      }
      if (e.keyCode === 13 && buffer.length > 3) {
        e.preventDefault();
        handleBarcodeScanRef.current(buffer, e.target);
        buffer = "";
        return;
      }
      // Enter in the search box after typing by hand (the buffer above only holds
      // fast, scanner-speed keystrokes).
      if (
        e.keyCode === 13 &&
        e.target &&
        e.target.getAttribute &&
        e.target.getAttribute("data-pos-search") === "true"
      ) {
        e.preventDefault();
        handleSearchEnterRef.current();
        buffer = "";
        return;
      }
      // Only printable characters are part of a barcode. Was: only Shift
      // was skipped, so CapsLock/Tab/Control (and a short "Enter") were
      // appended by name and broke the match.
      if (!e.key || e.key.length !== 1) {
        return;
      }
      buffer += e.key;
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        buffer = "";
      }, 100);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(resetTimer);
    };
  }, [isActive]);
  if (loading) {
    return (
      <main
        className={`${classes.content} ${(!isCartPersistent || cartOpen) ? classes.contentShift : ""}`}
        style={{ marginLeft: 0 }}
      >
        <Grid container spacing={2}>
          {Array.from({ length: 18 }).map((_, i) => (
            <Grid
              item
              xs={6}
              sm={6}
              key={i}
              className={`${classes.itemGrid} ${classes[`gridCols${gridCols}`] || classes.gridCols6}`}
              style={{
                opacity: 0,
                animation: `posSkeletonFadeIn 0.4s ease forwards`,
                animationDelay: `${i * 45}ms`,
              }}
            >
              <PosItemSkeleton />
            </Grid>
          ))}
        </Grid>
      </main>
    );
  }

  if (filteredItemsByCategory.length === 0) {
    const handleAddClick = () => {
      setItemId(null);
      setOpenItem(true);
    };
    return (
      <main
        className={`${classes.content} ${(!isCartPersistent || cartOpen) ? classes.contentShift : ""}`}
        style={{ marginLeft: 0 }}
      >
        <AddItem
          open={openItem}
          setOpen={setOpenItem}
          itemId={itemId}
          setItemId={setItemId}
          onCreated={handleItemCreated}
        />
        <div className={classes.emptyState}>
          <div className={classes.emptyStateIconButton}>
            <AddIcon className={classes.emptyStateIcon} />
          </div>
          <Typography className={classes.emptyStateTitle}>
            No items in this category yet
          </Typography>
          <Typography className={classes.emptyStateSubtitle}>
            Add your first item here to start selling from this category.
          </Typography>
          <Button
            variant="contained"
            className={classes.emptyStateButton}
            startIcon={<AddIcon />}
            onClick={handleAddClick}
          >
            Add Item
          </Button>
        </div>
      </main>
    );
  }

  return (
    <>
      {batchPick && (
        <BatchPickerDialog
          key={batchPick.item._id}
          open
          item={batchPick.item}
          needed={batchPick.needed}
          stockUnit={batchPick.item?.units?.stock?.unit?.abbreviation || ""}
          initialPicks={batchPick.initialPicks}
          freeQty
          startEmpty
          inCart={batchPick.base * batchPick.unitStock}
          onClose={() => setBatchPick(null)}
          onConfirm={confirmBatchPick}
        />
      )}
      <AddItem
        open={openItem}
        setOpen={setOpenItem}
        itemId={itemId}
        setItemId={setItemId}
        onCreated={handleItemCreated}
      />
      <main
        className={`${classes.content} ${(!isCartPersistent || cartOpen) ? classes.contentShift : ""}`}
        style={{ marginLeft: 0 }}
      >
        <Grid container spacing={2} className={classes.gridContainer}>
          <Grid
            item
            xs={6}
            sm={6}
            className={`${classes.itemGrid} ${classes[`gridCols${gridCols}`] || classes.gridCols6}`}
          >
            <ButtonBase centerRipple className={classes.itemButton}>
              <Card
                className={classes.addCard}
                onClick={() => {
                  setItemId(null);
                  setOpenItem(true);
                }}
              >
                <div className={classes.addCardIconWrap}>
                  <AddIcon className={classes.addCardIcon} />
                </div>
                <Typography className={classes.addCardLabel}>
                  Add Item
                </Typography>
              </Card>
            </ButtonBase>
          </Grid>
          {visibleItems.map((item) => (
            <ItemCard
              key={item._id}
              item={item}
              currency={currency}
              onSelect={handleItemSelect}
              gridCols={gridCols}
              hint={hints[item._id]}
              onPickBatch={handlePickBatch}
              canPick={!activeSessionId}
              showExpiry={showExpiry}
              showStock={showStock}
            />
          ))}
        </Grid>
      </main>
      {/* One shared dialog for whichever item was last clicked, instead of
          one full CommissionDialog instance per item in the catalogue (every
          one of which subscribes to CartContext/ShiftContext) - see
          ItemCard/handleItemSelect above. */}
      <CommissionDialog
        open={commissionDialogOpen}
        staff={staff}
        staffCommissionDetailEdit={staffCommissionDetails.find(
          (scd) => scd.serviceId === selectedService._id
        )}
        onClose={handleCommissionDialogClose}
        serviceId={selectedService._id}
        service={selectedService}
        openSnackbar={openSnackbar}
      />
    </>
  );
};

export default ItemCards;
